import React, { useMemo, useCallback } from 'react';
import { Tree } from 'syngenta-digital-cropwise-react-ui-kit';
import { ApplicationPropertyVO } from '../../../../hooks/useApplicationProperties';
import { SortOption } from '../PropertySortingDropdown';
import { sortByArchivedStatus, filterPropertiesBySearch } from './helpers';
import { TREE_PADDING, TREE_PADDING_BY_CROP } from './constants';
import FarmTreeItem from './components/FarmTreeItem';
import FieldTreeItem from './components/FieldTreeItem';
import CropZoneTreeItem from './components/CropZoneTreeItem';
import CropTreeItem from './components/CropTreeItem';

interface PropertiesTreeProps {
  properties: ApplicationPropertyVO[];
  treeState: {
    selectedKeys: string[];
    expandedKeys: string[];
    onExpand: (keys: string[]) => void;
  };
  handlers: {
    onFarmClick: (farmId: string, farmKey: string) => void;
    onFieldClick: (fieldId: string, fieldKey: string) => void;
    onCropClick?: (cropId: string, cropZoneIds: string[]) => void;
  };
  sortBy?: SortOption;
  searchTerm?: string;
}

// Helper function to build farm map from properties
const buildFarmMap = (properties: ApplicationPropertyVO[]) => {
  const farmMap = new Map<string, any>();

  // eslint-disable-next-line no-restricted-syntax
  for (const prop of properties) {
    if (!farmMap.has(prop.farmId)) {
      farmMap.set(prop.farmId, {
        id: prop.farmId,
        name: prop.farmName,
        archived: prop.farmArchived,
        fields: []
      });
    }

    const farm = farmMap.get(prop.farmId);
    if (farm) {
      let field = farm.fields.find((f: any) => f.id === prop.fieldId);
      if (!field) {
        field = {
          id: prop.fieldId,
          name: prop.fieldName,
          archived: prop.fieldArchived,
          geometry: prop.fieldGeometry,
          cropzones: [],
          totalAppliedArea: 0
        };
        farm.fields.push(field);
      }

      field.cropzones.push({
        id: prop.cropZoneId,
        name: prop.cropZoneName,
        cropName: prop.cropName,
        appliedArea: prop.appliedArea,
        archived: prop.cropZoneArchived,
        geometry: prop.cropZoneGeometry
      });
      field.totalAppliedArea += prop.appliedArea;
    }
  }

  return farmMap;
};

// Helper function to build crop map from properties (Group by Crop)
const buildCropMap = (properties: ApplicationPropertyVO[]) => {
  const cropMap = new Map<string, any>();

  // eslint-disable-next-line no-restricted-syntax
  for (const prop of properties) {
    const cropKey = prop.cropName || 'Unknown';

    if (!cropMap.has(cropKey)) {
      cropMap.set(cropKey, {
        id: `crop-${cropKey}`,
        name: cropKey,
        farms: []
      });
    }

    const crop = cropMap.get(cropKey);
    if (crop) {
      let farm = crop.farms.find((f: any) => f.id === prop.farmId);
      if (!farm) {
        farm = {
          id: prop.farmId,
          name: prop.farmName,
          archived: prop.farmArchived,
          fields: []
        };
        crop.farms.push(farm);
      }

      let field = farm.fields.find((f: any) => f.id === prop.fieldId);
      if (!field) {
        field = {
          id: prop.fieldId,
          name: prop.fieldName,
          archived: prop.fieldArchived,
          geometry: prop.fieldGeometry,
          cropzones: [],
          totalAppliedArea: 0
        };
        farm.fields.push(field);
      }

      field.cropzones.push({
        id: prop.cropZoneId,
        name: prop.cropZoneName,
        cropName: prop.cropName,
        appliedArea: prop.appliedArea,
        archived: prop.cropZoneArchived,
        geometry: prop.cropZoneGeometry
      });
      field.totalAppliedArea += prop.appliedArea;
    }
  }

  return cropMap;
};

// Helper function to create crop zone tree node
const createCropZoneNode = (
  cropZone: any,
  paddingClass: string,
  searchTerm?: string
) => {
  return {
    key: cropZone.id,
    className: 'crop-zone-node',
    title: (
      <CropZoneTreeItem
        name={cropZone.name}
        archivedState={{
          isArchived: cropZone.archived,
          showLabel: cropZone.archived
        }}
        appliedArea={cropZone.appliedArea}
        cropName={cropZone.cropName}
        geometry={cropZone.geometry}
        paddingClass={paddingClass}
        searchTerm={searchTerm}
      />
    )
  };
};

// Helper function to create field tree node
const createFieldNode = (
  field: any,
  config: {
    onFieldClick: (fieldId: string, fieldKey: string) => void;
    fieldPadding: string;
    cropZonePadding: string;
    parentKey?: string;
    searchTerm?: string;
  }
) => {
  const {
    onFieldClick,
    fieldPadding,
    cropZonePadding,
    parentKey,
    searchTerm
  } = config;

  // Use parent context for unique tree key, but keep real fieldId for clicks
  const fieldKey = parentKey ? `${field.id}-in-${parentKey}` : field.id;

  return {
    key: fieldKey,
    title: (
      <FieldTreeItem
        id={field.id}
        name={field.name}
        geometry={field.geometry}
        archivedState={{
          isArchived: field.archived,
          showLabel: field.archived
        }}
        stats={{
          cropZoneCount: field.cropzones.length,
          totalAcres: field.totalAppliedArea
        }}
        onClick={() => onFieldClick(field.id, fieldKey)}
        paddingClass={fieldPadding}
        searchTerm={searchTerm}
      />
    ),
    children: field.cropzones.map((cz: any) =>
      createCropZoneNode(cz, cropZonePadding, searchTerm)
    )
  };
};

// Helper function to create farm tree node
const createFarmNode = (
  farm: any,
  config: {
    onFarmClick: (farmId: string, farmKey: string) => void;
    onFieldClick: (fieldId: string, fieldKey: string) => void;
    padding: {
      farm: string;
      field: string;
      cropZone: string;
    };
    parentKey?: string;
    searchTerm?: string;
  }
) => {
  const { onFarmClick, onFieldClick, padding, parentKey, searchTerm } = config;

  const sortedFields = sortByArchivedStatus(farm.fields);
  const totalFarmAcres = sortedFields.reduce(
    (sum: number, f: any) => sum + f.totalAppliedArea,
    0
  );

  // Use parent context for unique tree key, but keep real farmId for clicks
  const farmKey = parentKey ? `${farm.id}-in-${parentKey}` : farm.id;

  return {
    key: farmKey,
    title: (
      <FarmTreeItem
        name={farm.name}
        isArchived={farm.archived}
        fieldCount={sortedFields.length}
        totalAcres={totalFarmAcres}
        onClick={() => onFarmClick(farm.id, farmKey)}
        paddingClass={padding.farm}
        searchTerm={searchTerm}
      />
    ),
    children: sortedFields.map((field: any) =>
      createFieldNode(field, {
        onFieldClick,
        fieldPadding: padding.field,
        cropZonePadding: padding.cropZone,
        parentKey: farmKey,
        searchTerm
      })
    )
  };
};

// Helper function to create crop tree node (for Group by Crop)
const createCropNode = (
  crop: any,
  config: {
    onFarmClick: (farmId: string, farmKey: string) => void;
    onFieldClick: (fieldId: string, fieldKey: string) => void;
    onCropClick: (cropId: string, cropZoneIds: string[]) => void;
    padding: {
      farm: string;
      field: string;
      cropZone: string;
    };
    searchTerm?: string;
  }
) => {
  const {
    onFarmClick,
    onFieldClick,
    onCropClick,
    padding,
    searchTerm
  } = config;

  const sortedFarms = sortByArchivedStatus(crop.farms);

  // Collect all crop zone IDs under this crop
  const allCropZoneIds: string[] = [];
  // eslint-disable-next-line no-restricted-syntax
  for (const farm of sortedFarms as any[]) {
    // eslint-disable-next-line no-restricted-syntax
    for (const field of farm.fields) {
      // eslint-disable-next-line no-restricted-syntax
      for (const cz of field.cropzones) {
        allCropZoneIds.push(cz.id);
      }
    }
  }

  const farmChildren = sortedFarms.map((farm: any) =>
    createFarmNode(farm, {
      onFarmClick,
      onFieldClick,
      padding,
      parentKey: crop.id,
      searchTerm
    })
  );

  return {
    key: crop.id,
    title: (
      <CropTreeItem
        name={crop.name}
        onClick={() => onCropClick(crop.id, allCropZoneIds)}
        searchTerm={searchTerm}
      />
    ),
    children: farmChildren
  };
};

const PropertiesTree: React.FC<PropertiesTreeProps> = ({
  properties,
  treeState,
  handlers,
  sortBy = SortOption.BY_FARM,
  searchTerm = ''
}) => {
  const { selectedKeys, expandedKeys, onExpand } = treeState;
  const { onFarmClick, onFieldClick, onCropClick } = handlers;

  // Filter properties based on search term
  const filteredProperties = useMemo(() => {
    return filterPropertiesBySearch(properties, searchTerm);
  }, [properties, searchTerm]);

  // Default crop click handler - selects all crop zones in the crop
  const handleCropClick = useCallback(
    (cropId: string, cropZoneIds: string[]) => {
      if (onCropClick) {
        onCropClick(cropId, cropZoneIds);
      }
    },
    [onCropClick]
  );

  // Transform properties into Tree structure based on sort option
  const treeData = useMemo(() => {
    if (sortBy === SortOption.BY_CROP) {
      // Group by Crop -> Farm -> Field -> CropZone
      const cropMap = buildCropMap(filteredProperties);
      const crops = Array.from(cropMap.values()).sort((a, b) =>
        a.name.localeCompare(b.name)
      );
      return crops.map(crop =>
        createCropNode(crop, {
          onFarmClick,
          onFieldClick,
          onCropClick: handleCropClick,
          padding: {
            farm: TREE_PADDING_BY_CROP.FARM,
            field: TREE_PADDING_BY_CROP.FIELD,
            cropZone: TREE_PADDING_BY_CROP.CROP_ZONE
          },
          searchTerm
        })
      );
    }

    // Default: Group by Farm -> Field -> CropZone
    const farmMap = buildFarmMap(filteredProperties);
    const sortedFarms = sortByArchivedStatus(Array.from(farmMap.values()));
    return sortedFarms.map(farm =>
      createFarmNode(farm, {
        onFarmClick,
        onFieldClick,
        padding: {
          farm: TREE_PADDING.FARM,
          field: TREE_PADDING.FIELD,
          cropZone: TREE_PADDING.CROP_ZONE
        },
        parentKey: undefined,
        searchTerm
      })
    );
  }, [
    filteredProperties,
    onFarmClick,
    onFieldClick,
    handleCropClick,
    sortBy,
    searchTerm
  ]);

  return (
    <Tree
      treeData={treeData}
      showIcon={false}
      selectedKeys={selectedKeys}
      expandedKeys={expandedKeys}
      onExpand={keys => onExpand(keys as string[])}
    />
  );
};

export default PropertiesTree;
